import { format, formatDistanceToNowStrict } from 'date-fns';

/** Indian Rupee formatter — compact for big numbers (2 decimal places). */
export function formatRupees(v: number, opts?: { compact?: boolean }): string {
  if (!Number.isFinite(v)) return '—';
  if (opts?.compact && Math.abs(v) >= 100_000) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(v);
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

/** kW / kVA / A / V — render with high precision (2 decimal places). */
export function formatNumber(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

/** Current in Amperes — 2 decimal precision. */
export function formatCurrent(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

/** Voltage in Volts — 2 decimal precision. */
export function formatVoltage(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

export function formatKwh(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} GWh`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(2)} MWh`;
  return `${v.toFixed(2)} kWh`;
}

export function formatKvah(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} GVAh`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(2)} MVAh`;
  return `${v.toFixed(2)} kVAh`;
}

export function formatPF(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '—';
  // BUG FIX: toFixed(2) rounds 0.9956 → "1.00" (impossible PF display).
  // 3 decimal places shows "0.996" / "0.988" — correct for power engineering.
  return v.toFixed(3);
}

export function formatPct(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '—';
  return `${v.toFixed(digits)}%`;
}

/** Compact "2 m ago", "just now" etc. */
export function formatRelative(d: Date | number | string): string {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);
  if (seconds < 30) return 'just now';
  return `${formatDistanceToNowStrict(date, { addSuffix: false })} ago`;
}

export function formatClock(d: Date): string {
  return format(d, 'EEE, d MMM · HH:mm');
}

export function formatCarbon(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(2)} t CO₂`;
  return `${v.toFixed(1)} kg CO₂`;
}
